import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import { Messaging, getToken, deleteToken } from '@angular/fire/messaging';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { Logger } from '../logger/logger.service';
import { environment } from '../../../../environments/environment';

const LOGGER = new Logger('FirebaseMessagingService');

const VAPID_KEY = environment.vapidKey;

export interface FcmMessage {
    notification?: {
        title?: string;
        body?: string;
        icon?: string;
    };
    data?: Record<string, string>;
    from?: string;
    collapseKey?: string;
}

@Injectable({
    providedIn: 'root',
})
export class FirebaseMessagingService {
    private readonly tokenSubject = new BehaviorSubject<string | null>(null);
    private readonly messageSubject = new BehaviorSubject<FcmMessage | null>(null);

    /** Observable du token FCM courant (null si pas encore obtenu ou permission refusée). */
    readonly token$ = this.tokenSubject.asObservable();

    /** Observable des messages reçus en foreground. */
    readonly message$ = this.messageSubject.asObservable();

    private readonly _injector = inject(Injector);

    constructor(private messaging: Messaging) {}

    /**
     * Demande la permission de notifications au navigateur puis récupère le FCM token.
     * Peut être appelé au démarrage de l'app — le navigateur affiche son dialog natif si besoin.
     * Si la permission a déjà été accordée/refusée, aucun dialog n'est affiché.
     *
     * @returns Observable<string | null> — le token FCM, ou null si refusé/erreur.
     */
    requestPermissionAndGetToken(): Observable<string | null> {
        if (!('Notification' in window)) {
            LOGGER.warn('Les notifications push ne sont pas supportées par ce navigateur.');
            return of(null);
        }

        if (Notification.permission === 'denied') {
            LOGGER.warn('La permission de notification a été refusée par l\'utilisateur.');
            return of(null);
        }

        return from(this._requestTokenInternal()).pipe(
            tap((token) => {
                if (token) {
                    LOGGER.info('Token FCM obtenu.');
                    this.tokenSubject.next(token);
                    localStorage.setItem('fcm_device_token', token);
                } else {
                    LOGGER.warn('Token FCM non obtenu (permission refusée ?).');
                }
            }),
            catchError((err) => {
                LOGGER.error('Erreur lors de l\'obtention du token FCM:', err);
                return of(null);
            })
        );
    }

    /**
     * Active l'écoute des messages en foreground (app ouverte).
     * Le SW transmet le payload via postMessage quand l'app est visible.
     * Appeler une seule fois au démarrage de l'app (ex: dans AppComponent.ngOnInit).
     */
    listenToForegroundMessages(): void {
        if (!('serviceWorker' in navigator)) return;

        navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data?.type !== 'FCM_MESSAGE') return;
            LOGGER.debug('Message FCM reçu en foreground (via SW):', event.data.payload);
            this.messageSubject.next(event.data.payload as FcmMessage);
        });

        LOGGER.debug('Écoute des messages FCM en foreground activée.');
    }

    /**
     * Supprime le token FCM enregistré côté Firebase.
     * Utiliser pour désinscrire l'utilisateur des notifications.
     */
    deleteToken(): Observable<boolean> {
        return from(runInInjectionContext(this._injector, () => deleteToken(this.messaging))).pipe(
            tap(() => {
                LOGGER.info('Token FCM supprimé.');
                this.tokenSubject.next(null);
                localStorage.removeItem('fcm_device_token');
            }),
            catchError((err) => {
                LOGGER.error('Erreur lors de la suppression du token FCM:', err);
                return of(false);
            })
        );
    }

    /** Retourne le token courant sans déclencher de nouvelle demande. */
    getCurrentToken(): string | null {
        return this.tokenSubject.getValue();
    }

    /** Retourne le statut actuel de la permission de notification du navigateur. */
    getPermissionStatus(): NotificationPermission {
        if (!('Notification' in window)) return 'denied';
        return Notification.permission;
    }

    // ─── Méthodes privées ────────────────────────────────────────────────────────

    private async _requestTokenInternal(): Promise<string | null> {
        let permission = Notification.permission;
        if (permission !== 'granted') {
            permission = await Notification.requestPermission();
        }

        if (permission !== 'granted') {
            LOGGER.warn(`Permission notifications : ${permission}`);
            return null;
        }

        const swRegistration = await this._registerServiceWorker();

        const token = await runInInjectionContext(this._injector, () =>
            getToken(this.messaging, {
                vapidKey: VAPID_KEY,
                ...(swRegistration ? { serviceWorkerRegistration: swRegistration } : {}),
            })
        );

        return token ?? null;
    }

    private async _registerServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
        if (!('serviceWorker' in navigator)) {
            LOGGER.warn('Service Worker non supporté : les notifications en arrière-plan seront indisponibles.');
            return undefined;
        }

        try {
            const existing = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
            if (existing?.active) {
                LOGGER.debug('Service Worker Firebase déjà actif.');
                return existing;
            }

            const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
                scope: '/',
            });

            // Attend que le SW soit actif avant de continuer
            await new Promise<void>((resolve) => {
                if (registration.active) { resolve(); return; }
                const sw = registration.installing ?? registration.waiting;
                sw?.addEventListener('statechange', (e) => {
                    if ((e.target as ServiceWorker).state === 'activated') resolve();
                });
            });

            LOGGER.debug('Service Worker Firebase enregistré avec succès.');
            return registration;
        } catch (err) {
            LOGGER.error('Échec de l\'enregistrement du Service Worker Firebase:', err);
            return undefined;
        }
    }
}
